import { ILocalization, ILocalizationToken } from '../../../../../../../application/language/ILocalization';
import { inject } from '../../../../../../../common/container/inject';
import { IUserProjectRoleModel } from '../../../../../../../service/projectRole/entity/IUserProjectRoleModel';
import { IUserProjectRoleSearch } from '../../../../../../../service/projectRole/entity/IUserProjectRoleSearch';
import { injectRootService } from '../../../../../../../service/RootServiceFactory';
import { UserSystemRoleModelPermissionMap } from '../../../../../../../service/systemRole/entity/actions/UserSystemRoleModelPermissionMap';
import TableHeader from '../../../../../../design/dataTable/parts/appBar/TableHeader';
import { DataTableDomain } from '../../../../../../design/dataTable/store/DataTableDomain';
import { DefaultTableSlots } from '../../../../../../design/dataTable/store/DataTableUI';
import { IMainLayoutDomainStore } from '../../../../../../layout/main/store/domain/IMainLayoutDomainStore';
import { IUnitCardModel } from '../../interface/IUnitCardModel';
import { IUnitListAdminPageDomain } from '../../learningUnitList/store/IUnitListAdminPageDomain';
import { UnitListAdminPageDomain } from '../../learningUnitList/store/UnitListAdminPageDomain';
import { UserProjectRoleContextMenuDomain } from './context/UserProjectRoleContextMenuDomain';
import { UserProjectRoleAdminUI } from './UserProjectRoleAdminUI';

export class CustomAppBar {
  Toolbar = TableHeader;
}

export class UserProjectRoleAdminDomain extends DataTableDomain<IUserProjectRoleModel, IUserProjectRoleSearch> {
  private unitListDomain: IUnitListAdminPageDomain;

  constructor(
    layoutDomain: IMainLayoutDomainStore,
    public rootPrivateServices = injectRootService(layoutDomain.serviceType.value),
    public i18n = inject<ILocalization>(ILocalizationToken),
  ) {
    super(layoutDomain, rootPrivateServices.projectRole.entity);
    this.ui = new UserProjectRoleAdminUI(this, null);
    this.unitListDomain = new UnitListAdminPageDomain(layoutDomain);
    this.contextMenuDomain = new UserProjectRoleContextMenuDomain({
      entityService: rootPrivateServices.projectRole.entity,
      dataTableDomain: this,
      layoutDomain,
    });
    this.ui.features.setValue({
      isCanBulkActivate: false,
      isCanBulkDeactivate: false,
      isCanCreate: true,
      isCanDelete: true,
      isCanEdit: true,
      isCanRedirectDetail: false,
    });

    this.ui.tableSlots.setEntity(new CustomAppBar() as DefaultTableSlots);
    this.ui.tableTitle?.setValue(this.i18n.translate('phrases.usersManagement'));
    this.ui.tableSubtitle?.setValue(this.i18n.translate('phrases.projectRoles'));
    this.ui.tableAddButtonName?.setValue(this.i18n.translate('phrases.addRole'));
  }

  async loadData() {
    await super.loadData();
    await this.loadUnitsMapping();
  }

  private async loadUnitsMapping() {
    await this.unitListDomain.loadData();

    const groupedUnits = this.unitListDomain.ui.filtredGroupedUnits.list;
    if (!groupedUnits.length) {
      return;
    }

    const allUnits: IUnitCardModel[] = groupedUnits.flatMap((group) => {
      const { unitsList } = group;
      return Array.isArray(unitsList) ? unitsList : [];
    });

    const allPublishedUnits = allUnits.filter((unit) => unit.isPublished);

    const rolesMappedToUnits = this.ui.rows.entities.list.map((item) => ({
      ...item,
      requiredUnits: allPublishedUnits
        .filter((unit) => (unit.settings.teamRolesIds ?? []).includes(item.id as string))
        .map((unit) => unit.settings.titleHeading),
    }));

    this.ui.rows.entities.setList(rolesMappedToUnits);
    this.ui.rows.filteredEntities.setList(rolesMappedToUnits);
  }

  async setPermissions() {
    const isCanCreate = this.layoutDomain.userHaveAnyAccess([
      UserSystemRoleModelPermissionMap['settings-user-project-role-create'],
    ]);
    const isCanDelete = this.layoutDomain.userHaveAnyAccess([
      UserSystemRoleModelPermissionMap['settings-user-project-role-delete'],
    ]);
    const isCanUpdate = this.layoutDomain.userHaveAnyAccess([
      UserSystemRoleModelPermissionMap['settings-user-project-role-update'],
    ]);
    this.ui.isCanEdit.setValue(isCanUpdate);
    this.ui.isCanCreate.setValue(isCanCreate);
    this.ui.isCanDelete.setValue(isCanDelete);
  }

  onHandleAdd = () => {
    this.router.goTo('/settings/user/project/role/new');
  };
}
